import React, { useState } from 'react';
import SigninWrapper from '../wrapper/SigninWrapper';
import Button from '../button/Button';
import InputField from '../Form/InputField';
import { useFormik } from 'formik';
import { emailSchema, resetPasswordSchema } from '../../schema';
import { forgotPasswordAPI, resetPasswordAPI } from '../../API/auth/PasswordAPI';
import toast from 'react-hot-toast';
import ButtonLoader from '../button/ButtonLoader';
import { useNavigate } from 'react-router-dom';

const initialState = {
    email: ""
}

const resetPasswordInitialState = {
    code: "",
    password: "",
    confirmPassword: "",
}

const EmailVerification = ({ onNext, setStepValues }) => {
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [sentEmail, setSentEmail] = useState("");
    const [resettingPassword, setResettingPassword] = useState(false);
    const navigate = useNavigate();

    const { values, errors, touched, handleBlur, handleChange, handleSubmit } = useFormik({
        initialValues: initialState,
        validationSchema: emailSchema,
        onSubmit: async (values, { resetForm }) => {
            setLoading(true);
            try {
                await forgotPasswordAPI({ email: values.email });
                toast.success("If an account with that email exists, a verification code has been sent to your email.");
                setStepValues({
                    email: values.email,
                });
                setSentEmail(values.email);
                resetForm();
                setEmailSent(true);
            } catch (err) {
                console.error(err);
                // Error message is already shown by the API or toast
            } finally {
                setLoading(false);
            }
        }
    });

    const { 
        values: resetValues, 
        errors: resetErrors, 
        touched: resetTouched, 
        handleBlur: handleResetBlur, 
        handleChange: handleResetChange, 
        handleSubmit: handleResetSubmit 
    } = useFormik({
        initialValues: resetPasswordInitialState,
        validationSchema: resetPasswordSchema,
        onSubmit: async (values, { resetForm }) => {
            setResettingPassword(true);
            try {
                await resetPasswordAPI({
                    email: sentEmail,
                    code: values.code,
                    password: values.password,
                });
                toast.success("Password reset successfully! You can now login with your new password.");
                resetForm();
                setTimeout(() => {
                    navigate('/signin');
                }, 2000);
            } catch (err) {
                console.error(err);
                if (err.response?.data?.error?.message) {
                    toast.error(err.response.data.error.message);
                } else {
                    toast.error("Error resetting password. Please try again.");
                }
            } finally {
                setResettingPassword(false);
            }
        },
    });

    if (emailSent) {
        return (
            <SigninWrapper headingText={'Check Your Email'}>
                <div className="space-y-4 md:space-y-6">
                    <div className="text-center">
                        <div className="text-green-600 mb-4">
                            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <p className="text-gray-700">
                            If an account with that email exists, we've sent a verification code to <strong>{sentEmail}</strong>.
                        </p>
                        <p className="text-gray-600 text-sm mt-2">
                            Please check your email and enter the verification code below to reset your password.
                        </p>
                        <p className="text-gray-500 text-sm mt-1">
                            The code will expire in 30 minutes.
                        </p>
                    </div>

                    <form onSubmit={handleResetSubmit} className="space-y-4 md:space-y-6 mt-6">
                        <InputField
                            label={"Verification Code"}
                            id={"code"}
                            name={"code"}
                            type={"text"}
                            placeholder={"Enter 6-digit code"}
                            maxLength={6}
                            value={resetValues.code}
                            onChange={handleResetChange}
                            onBlur={handleResetBlur}
                            error={resetErrors.code}
                            touch={resetTouched.code}
                        />

                        <InputField
                            label={"New Password"}
                            id={"password"}
                            name={"password"}
                            type={"password"}
                            placeholder={"Enter your new password"}
                            value={resetValues.password}
                            onChange={handleResetChange}
                            onBlur={handleResetBlur}
                            error={resetErrors.password}
                            touch={resetTouched.password}
                        />

                        <InputField
                            label={"Confirm New Password"}
                            id={"confirmPassword"}
                            name={"confirmPassword"}
                            type={"password"}
                            placeholder={"Confirm your new password"}
                            value={resetValues.confirmPassword}
                            onChange={handleResetChange}
                            onBlur={handleResetBlur}
                            error={resetErrors.confirmPassword}
                            touch={resetTouched.confirmPassword}
                        />

                        {resettingPassword ? (
                            <div className="flex justify-center items-center">
                                <ButtonLoader />
                            </div>
                        ) : (
                            <Button type={"submit"} text={"Reset Password"} />
                        )}

                        <div className="text-center pt-2">
                            <a href="/signin" className="text-[--primary] hover:underline text-sm">
                                Back to Login
                            </a>
                        </div>
                    </form>
                </div>
            </SigninWrapper>
        );
    }

    return (
        <>
            <SigninWrapper
                headingText={'Enter Your Email'}
            >
                <form
                    onSubmit={handleSubmit}
                    className="space-y-4 md:space-y-6" >
                    <InputField
                        label={'Your email'}
                        id={'email'}
                        name={'email'}
                        type={'text'}
                        placeholder={'name@company.com'}
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.email}
                        touch={touched.email}
                    />
                    {
                        loading ? (
                            <ButtonLoader />
                        ) : (
                            <Button
                                type={'submit'}
                                text={'Verify email'}
                            />
                        )
                    }
                </form>
            </SigninWrapper>
        </>
    )
}

export default EmailVerification